import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  MessageSquare, 
  Handshake,
  Sparkles,
  Globe,
  Shield,
  Zap,
  Target,
  Mail
} from 'lucide-react';

const CyberSponsor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToFounders = () => {
    document.getElementById('founders-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const stripeLink = "https://buy.stripe.com/9B614o6GL0HnfDTcZz9oc07";

  const sponsors = [
    { name: 'Okta Ventures', logo: 'https://cdn.worldvectorlogo.com/logos/okta-2.svg' },
    { name: 'Auth0', logo: 'https://cdn.worldvectorlogo.com/logos/auth0-logo.svg' },
    { name: 'Chargebee', logo: 'https://cdn.worldvectorlogo.com/logos/chargebee-1.svg' },
  ];

  const buyers = [
    { name: 'Microsoft', logo: 'https://cdn.worldvectorlogo.com/logos/microsoft-5.svg' },
    { name: 'NVIDIA', logo: 'https://cdn.worldvectorlogo.com/logos/nvidia.svg' },
    { name: 'GitHub', logo: 'https://cdn.worldvectorlogo.com/logos/github-icon-1.svg' },
    { name: 'Okta', logo: 'https://cdn.worldvectorlogo.com/logos/okta-2.svg' },
    { name: 'Fastly', logo: 'https://cdn.worldvectorlogo.com/logos/fastly.svg' },
    { name: 'Axon', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Axon_Enterprise_logo.svg/200px-Axon_Enterprise_logo.svg.png' },
    { name: 'Five9', logo: 'https://cdn.worldvectorlogo.com/logos/five9-1.svg' },
  ];

  const vcs = [
    { name: 'Y Combinator', logo: 'https://cdn.worldvectorlogo.com/logos/y-combinator.svg' },
    { name: 'a16z', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Andreessen_Horowitz_logo_stacked.svg/200px-Andreessen_Horowitz_logo_stacked.svg.png' },
    { name: 'Lightspeed', logo: 'https://cdn.worldvectorlogo.com/logos/lightspeed-1.svg' },
    { name: 'Accel', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Accel_%28company%29_logo.svg/200px-Accel_%28company%29_logo.svg.png' },
  ];

  const testimonials = [
    {
      quote: "TFI has helped us attract great founders, nurture our executive network, and build events that truly add value to founders.",
      name: "Austin Arensberg",
      title: "Head of Okta Ventures",
      avatar: "AA"
    },
    {
      quote: "We just decided to expand our contract with TFI because of the quality of founders we've seen.",
      name: "Eli Rabek",
      title: "Director of Startups at Auth0",
      avatar: "ER"
    },
    {
      quote: "Brilliant concept, all signal no noise.",
      name: "Cybersecurity Founder",
      title: "YC W24",
      avatar: "CF"
    }
  ];

  const founders = [
    { name: 'CAST AI', category: 'Cloud Infrastructure', url: 'https://cast.ai' },
    { name: 'Valence Security', category: 'SaaS Security', url: 'https://valencesecurity.com' },
    { name: 'Seraphic Security', category: 'Browser Security', url: 'https://seraphicsecurity.com' },
    { name: 'Clutch Security', category: 'Identity Security', url: 'https://clutch.security' },
    { name: 'Kodem Security', category: 'AppSec', url: 'https://kodem.io' },
    { name: 'Repello AI', category: 'AI Security', url: 'https://repello.ai' },
    { name: 'BforeAI', category: 'Threat Intelligence', url: 'https://bfore.ai' },
    { name: 'Sedai', category: 'Cloud Infrastructure', url: 'https://sedai.io' },
    { name: 'CloudEagle.ai', category: 'SaaS Management', url: 'https://cloudeagle.ai' },
    { name: 'Sola Security', category: 'AI Security', url: 'https://sola.security' },
    { name: 'Straiker', category: 'AI Security', url: 'https://straiker.ai' },
    { name: 'Netdata', category: 'DevSecOps', url: 'https://netdata.cloud' },
  ];

  const steps = [
    { icon: Shield, title: 'Cybersecurity founders apply', desc: 'High-growth startups submit applications' },
    { icon: Target, title: 'CISOs select priority problems', desc: 'Enterprise buyers choose focus areas' },
    { icon: Zap, title: 'Founders pitch live solutions', desc: 'Direct presentations to security leaders' },
    { icon: Handshake, title: 'Breakout sessions', desc: 'Deeper conversations and connections' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute w-[800px] h-[800px] rounded-full opacity-20 blur-[120px] transition-all duration-1000"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
            left: mousePosition.x - 400,
            top: mousePosition.y - 400,
          }}
        />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Minimal Header */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex justify-between items-center">
          <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-white">The Founder Initiative</span>
          </a>
          <a 
            href={stripeLink}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium transition-all"
          >
            Become a Sponsor
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 mb-6 px-4 py-1.5 text-sm">
            Cybersecurity Buyer Pitch Events
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
            Meet the cybersecurity founders{' '}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">
              actively trying to win CISOs.
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
            The Founder Initiative runs curated cybersecurity buyer pitch events where high-growth 
            founders get direct feedback from enterprise security leaders.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-12">
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm">Sharpen enterprise positioning</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-slate-600 rounded-full" />
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm">Build trust with CISOs</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-slate-600 rounded-full" />
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle className="w-4 h-4 text-blue-400" />
              <span className="text-sm">Create real buyer conversations</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={stripeLink}>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 text-base px-8 py-6 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
              >
                Become a Supporting Sponsor — $5,000
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            <Button 
              size="lg" 
              variant="outline"
              onClick={scrollToFounders}
              className="border-white/20 text-white hover:bg-white/5 px-8 py-6 rounded-xl"
            >
              See Example Founders
            </Button>
          </div>
        </div>
      </section>

      {/* Social Proof Logos */}
      <section className="relative z-10 py-20 px-6 border-y border-white/5 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-sm text-slate-500 uppercase tracking-widest mb-12">
            Trusted by founders, buyers, and sponsors
          </p>

          {/* Sponsors Row */}
          <div className="mb-10">
            <p className="text-xs text-slate-600 uppercase tracking-wider text-center mb-4">Sponsors</p>
            <div className="flex justify-center items-center gap-12 flex-wrap opacity-60 hover:opacity-80 transition-opacity">
              {sponsors.map((s) => (
                <div key={s.name} className="h-8 flex items-center">
                  <img src={s.logo} alt={s.name} className="h-6 sm:h-7 object-contain filter brightness-0 invert opacity-70" />
                </div>
              ))}
            </div>
          </div>

          {/* Buyers Row */}
          <div className="mb-10">
            <p className="text-xs text-slate-600 uppercase tracking-wider text-center mb-4">Buyers</p>
            <div className="flex justify-center items-center gap-8 sm:gap-12 flex-wrap opacity-60 hover:opacity-80 transition-opacity">
              {buyers.map((b) => (
                <div key={b.name} className="h-8 flex items-center">
                  <img src={b.logo} alt={b.name} className="h-5 sm:h-6 object-contain filter brightness-0 invert opacity-70" />
                </div>
              ))}
            </div>
          </div>

          {/* VCs Row */}
          <div>
            <p className="text-xs text-slate-600 uppercase tracking-wider text-center mb-4">VC / Founder Ecosystem</p>
            <div className="flex justify-center items-center gap-12 flex-wrap opacity-60 hover:opacity-80 transition-opacity">
              {vcs.map((v) => (
                <div key={v.name} className="h-8 flex items-center">
                  <img src={v.logo} alt={v.name} className="h-5 sm:h-6 object-contain filter brightness-0 invert opacity-70" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card 
                key={i} 
                className="bg-gradient-to-br from-slate-900/80 to-slate-900/40 border-white/5 hover:border-blue-500/30 transition-all group"
              >
                <CardContent className="p-6">
                  <div className="mb-4">
                    <svg className="w-8 h-8 text-blue-500/30" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                  </div>
                  <p className="text-slate-300 mb-6 leading-relaxed text-sm">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-sm font-semibold">
                      {t.avatar}
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.title}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Valuable Section */}
      <section className="relative z-10 py-24 px-6 bg-gradient-to-b from-blue-950/20 via-transparent to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How sponsors use TFI to create{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                real customer opportunities
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Card 1 */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-900/60 border-white/5 hover:border-blue-500/30 transition-all group overflow-hidden">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">1-Click Offer Signup</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Founders can opt into your offer directly during registration. Strong cybersecurity founders 
                  actively searching for GTM, advisory, and enterprise buyer support.
                </p>
                {/* Visual Mockup */}
                <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 rounded bg-blue-500/20 border border-blue-500/40" />
                    <span className="text-xs text-slate-400">Receive sponsor partner offers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500 flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-slate-300">Connect with Cyber Synapse</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2 */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-900/60 border-white/5 hover:border-blue-500/30 transition-all group overflow-hidden">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:bg-cyan-500/20 transition-colors">
                  <Globe className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Founder-Led Social Branding</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Finalists and participating founders promote their selection on LinkedIn leading up to the event, 
                  creating organic visibility around your brand.
                </p>
                {/* Social Post Mockup */}
                <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500" />
                    <span className="text-xs text-slate-300 font-medium">Founder • 2h</span>
                  </div>
                  <p className="text-xs text-slate-400">Excited to pitch at TFI's CISO event, sponsored by <span className="text-blue-400">@CyberSynapse</span> 🚀</p>
                </div>
              </CardContent>
            </Card>

            {/* Card 3 */}
            <Card className="bg-gradient-to-br from-slate-900 to-slate-900/60 border-white/5 hover:border-blue-500/30 transition-all group overflow-hidden">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Facilitated Introductions</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  We directly introduce sponsors to qualified cybersecurity founders and enterprise buyers post-event.
                </p>
                {/* Intro Email Mockup */}
                <div className="bg-slate-800/50 rounded-lg p-3 border border-white/5">
                  <div className="text-xs text-slate-500 mb-1">To: founder@startup.com</div>
                  <div className="text-xs text-slate-500 mb-2">Subject: <span className="text-slate-300">Intro: Ben @ Cyber Synapse</span></div>
                  <div className="h-px bg-white/5 mb-2" />
                  <p className="text-xs text-slate-400">Based on your pitch, I wanted to connect you with...</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Highlighted Statement */}
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl px-8 py-5">
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl">
                <span className="text-white font-medium">The goal is not generic event exposure.</span>
                <br />
                The goal is high-quality relationships with cybersecurity founders actively trying to reach enterprise buyers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Example Founders */}
      <section id="founders-section" className="relative z-10 py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Example cybersecurity startups from past applicant pools
            </h2>
            <p className="text-slate-400">
              Serious founders building enterprise security solutions
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {founders.map((f, i) => (
              <a 
                key={i}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <Card className="bg-slate-900/50 border-white/5 hover:border-blue-500/30 hover:bg-slate-900/80 transition-all h-full">
                  <CardContent className="p-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-800 to-slate-700 flex items-center justify-center mb-3 group-hover:from-blue-900/50 group-hover:to-cyan-900/50 transition-all">
                      <Shield className="w-5 h-5 text-slate-400 group-hover:text-blue-400 transition-colors" />
                    </div>
                    <h4 className="font-medium text-white text-sm mb-1 group-hover:text-blue-400 transition-colors">
                      {f.name}
                    </h4>
                    <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-xs">
                      {f.category}
                    </Badge>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* How Events Work */}
      <section className="relative z-10 py-24 px-6 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How the buyer pitches work
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="text-center group">
                <div className="relative mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 flex items-center justify-center mx-auto group-hover:border-blue-500/40 group-hover:from-blue-500/20 group-hover:to-cyan-500/20 transition-all">
                    <step.icon className="w-7 h-7 text-blue-400" />
                  </div>
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 left-full w-full h-px bg-gradient-to-r from-blue-500/30 to-transparent -translate-y-1/2" />
                  )}
                </div>
                <div className="text-xs text-blue-400 font-semibold mb-2">Step {i + 1}</div>
                <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                <p className="text-xs text-slate-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          {/* Glow effect */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[600px] h-[400px] bg-blue-500/20 rounded-full blur-[120px]" />
          </div>

          <div className="relative">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Become part of the{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                cybersecurity founder ecosystem.
              </span>
            </h2>

            <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
              A lightweight way to build relationships with high-intent cybersecurity founders 
              and enterprise security leaders.
            </p>

            <div className="flex flex-col items-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Access cybersecurity founders actively trying to improve GTM</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Build relationships with CISOs and security buyers</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Create founder pipeline for advisory/media/research offerings</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-blue-400" />
                <span className="text-sm">Designed as a simple MVP partnership first</span>
              </div>
            </div>

            <a href={stripeLink}>
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 text-lg px-10 py-7 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
              >
                Become a Supporting Sponsor — $5,000
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>

            <p className="text-sm text-slate-500 mt-6">
              Limited supporting sponsor slots per event.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm text-slate-400">The Founder Initiative</span>
          </div>
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} The Founder Initiative. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CyberSponsor;
